import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DashboardInfo, TenantInfo } from '../model/dashboard.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/Dashboard';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the dashboard information for the current context.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty structure when the API responds with 204 No Content.
   */
  getDashboardInfo(): Observable<DashboardInfo> {
    return this.http
      .get<DashboardInfo | null>(this.apiUrl + '/DashboardInfo', this.httpOptions)
      .pipe(
        map((jsonData) => jsonData ?? { tenants: [], latestImportRuns: [], latestPlanningRecords: [] }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of<DashboardInfo>({ tenants: [], latestImportRuns: [], latestPlanningRecords: [] });
          }
          return throwError(() => err);
        })
      );
  }

  /**
   * Returns the onboarding/tenant info for the current context.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty list when the API responds with 404/204.
   */
  getTenantInfo(): Observable<TenantInfo[]> {
    return this.http
      .get<TenantInfo[] | null>(this.apiUrl + '/TenantInfo', this.httpOptions)
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of<TenantInfo[]>([]);
          }
          return throwError(() => err);
        })
      );
  }
}
